import { useState, FC } from "react";
import { StyleSheet, View } from "react-native";
import EnrollmentLinkManager from "../../../components/adminDashboard/EnrollmentLinkManager/EnrollmentLinkManager";
import ConfirmationModal from "../../../components/general/ConfirmationModal/ConfirmationModal";
import CustomAdminModal from "../../../components/common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { useForm, Controller } from "react-hook-form";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import { z } from "zod";

import {
  useCreateEnrollmentLinkMutation,
  useDeleteEnrollmentLinkMutation,
  useGetEnrollmentLinkQuery,
  useUpdateEnrollmentLinkMutation,
} from "@axio/graphql/generated/graphql";
import { zodResolver } from "@hookform/resolvers/zod";
import { Checked, UnChecked } from "../../../components/svgs/common";

const DEFAULT_EXPIRES_IN_DAYS = 60;
const DEFAULT_MAX_ENROLLMENTS = 1;

interface EnrollmentLinkManagerProps {
  courseId: string;
}

enum ModalMode {
  HIDDEN = "hidden",
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
  ERROR = "error",
  SUCCESS = "success",
}

const formSchema = z
  .object({
    maxEnrollments: z
      .number({
        required_error: "Please enter the maximum number of enrollments.",
        invalid_type_error: "Please enter a valid number for maximum enrollments.",
      })
      .int("Maximum enrollments must be a whole number.")
      .min(DEFAULT_MAX_ENROLLMENTS, { message: "Maximum enrollments must be at least 1." }),
    hasExpiration: z.boolean(),
    expiresInDays: z.preprocess(
      (value) => {
        if (value === "" || value === null || value === undefined) {
          return undefined;
        }
        return Number(value);
      },
      z
        .number({
          invalid_type_error: "Please enter a valid number of days.",
        })
        .int("Expiration days must be a whole number.")
        .min(1, { message: "Expiration days must be at least 1." })
        .optional()
    ),
  })
  .refine(
    (data) => {
      if (data.hasExpiration && data.expiresInDays === undefined) {
        return false;
      }
      return true;
    },
    {
      message: "Please enter the number of days before the link expires.",
      path: ["expiresInDays"],
    }
  );

type EnrollmentLinkFormData = z.infer<typeof formSchema>;

const EnrollmentLinkManagerContainer: FC<EnrollmentLinkManagerProps> = ({ courseId }) => {
  const [modalMode, setModalMode] = useState<ModalMode>(ModalMode.HIDDEN);
  const [successMessage, setSuccessMessage] = useState("");

  const {
    handleSubmit: formHandleSubmit,
    reset,
    control,
    formState: { isSubmitting, errors },
    watch,
    setValue,
    clearErrors,
  } = useForm<EnrollmentLinkFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      maxEnrollments: DEFAULT_MAX_ENROLLMENTS,
      hasExpiration: false,
      expiresInDays: undefined,
    },
  });

  const {
    data: enrollmentLinkData,
    loading: enrollmentLinkLoading,
    refetch: refetchEnrollmentLink,
  } = useGetEnrollmentLinkQuery({
    variables: { courseId },
    skip: !courseId,
  });

  const hasExpiration = watch("hasExpiration");
  const enrollmentLink = enrollmentLinkData?.getEnrollmentLink?.data;

  const [createEnrollmentLink] = useCreateEnrollmentLinkMutation();
  const [updateEnrollmentLink] = useUpdateEnrollmentLinkMutation();
  const [deleteEnrollmentLink] = useDeleteEnrollmentLinkMutation();

  const handleCreateLink = () => {
    setModalMode(ModalMode.CREATE);
    reset({ maxEnrollments: DEFAULT_MAX_ENROLLMENTS, hasExpiration: false, expiresInDays: undefined });
  };

  const handleUpdateLink = () => {
    setModalMode(ModalMode.UPDATE);
    const existingMax = enrollmentLinkData?.getEnrollmentLink?.data?.maxEnrollments;
    let days = undefined;
    if (enrollmentLink?.expiresAt) {
      const now = new Date();
      const expiresAt = new Date(enrollmentLink.expiresAt);
      const remainingDays = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      days = remainingDays <= 0 ? DEFAULT_EXPIRES_IN_DAYS : remainingDays;
    }
    reset({
      maxEnrollments: Number(existingMax ?? DEFAULT_MAX_ENROLLMENTS),
      hasExpiration: days ? true : false,
      expiresInDays: days ? Number(days) : undefined,
    });
  };

  const onSubmit = formHandleSubmit(async (values: EnrollmentLinkFormData) => {
    const { maxEnrollments, expiresInDays, hasExpiration } = values;

    try {
      if (modalMode === ModalMode.CREATE) {
        const response = await createEnrollmentLink({
          variables: {
            input: {
              courseId,
              maxEnrollments: maxEnrollments,
              ...(hasExpiration && { expiresInDays: expiresInDays }),
            },
          },
        });

        if (response.data?.createEnrollmentLink?.success) {
          setSuccessMessage("Enrollment link generated successfully!");
          setModalMode(ModalMode.SUCCESS);
          refetchEnrollmentLink();
        } else {
          setModalMode(ModalMode.ERROR);
        }
      } else {
        if (!enrollmentLink?.id) {
          setModalMode(ModalMode.ERROR);
          return;
        }

        const response = await updateEnrollmentLink({
          variables: {
            id: enrollmentLink.id,
            input: {
              maxEnrollments: maxEnrollments,
              ...(hasExpiration && { expiresInDays: expiresInDays }),
            },
          },
        });

        if (response.data?.updateEnrollmentLink?.success) {
          setSuccessMessage("Enrollment link updated successfully!");
          setModalMode(ModalMode.SUCCESS);
          refetchEnrollmentLink();
        } else {
          setModalMode(ModalMode.ERROR);
        }
      }
    } catch (error) {
      setModalMode(ModalMode.ERROR);
    }
  });

  const handleDeleteLink = async () => {
    try {
      await deleteEnrollmentLink({ variables: { id: enrollmentLinkData?.getEnrollmentLink?.data?.id } });
      refetchEnrollmentLink();
      setSuccessMessage("Enrollment link deleted successfully!");
      setModalMode(ModalMode.SUCCESS);
    } catch (error) {
      setModalMode(ModalMode.ERROR);
    }
  };

  return (
    <>
      <EnrollmentLinkManager
        enrollmentLink={enrollmentLinkData?.getEnrollmentLink?.data}
        handleCreateLink={handleCreateLink}
        handleUpdateLink={handleUpdateLink}
        handleDeleteLink={() => setModalMode(ModalMode.DELETE)}
        isLoading={enrollmentLinkLoading}
      />
      <CustomAdminModal
        visible={modalMode === ModalMode.CREATE || modalMode === ModalMode.UPDATE}
        onRequestClose={() => setModalMode(ModalMode.HIDDEN)}
        title={modalMode === ModalMode.UPDATE ? "Update Course Enrollment Link" : "Generate Course Enrollment Link"}
        ariaLabel={
          modalMode === ModalMode.UPDATE
            ? "Update course enrollment link modal"
            : "Generate course enrollment link modal"
        }
        style={styles.modal}>
        <View style={styles.container}>
          <CustomSpacing type="vertical" size="l" />
          <View style={styles.inputContainer}>
            <CustomText
              text="Maximum number of enrollments"
              size="m"
              weight="medium"
              useTranslationText={false}
              style={styles.label}
            />
            <CustomText
              text="(Required - must be at least 1)"
              size="s"
              weight="regular"
              useTranslationText={false}
              style={styles.helperText}
            />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="maxEnrollments"
              render={({ field }) => (
                <CustomTextInput
                  value={field.value ?? ""}
                  onChangeText={(value) => field.onChange(Number(value.replace(/[^\d]/g, "")))}
                  placeholder="Enter number of enrollments"
                  keyboardType="numeric"
                  style={styles.input}
                />
              )}
            />
            {!!errors.maxEnrollments?.message && (
              <CustomText
                text={String(errors.maxEnrollments.message)}
                size="s"
                style={styles.errorText}
                useTranslationText={false}
              />
            )}
          </View>
          <CustomSpacing type="vertical" size="m" />
          <View style={styles.inputContainer}>
            <View style={styles.checkboxRow}>
              <Controller
                control={control}
                name="hasExpiration"
                render={({ field }) => (
                  <CustomButton
                    styleType="transparent"
                    role="checkbox"
                    aria-checked={field.value}
                    aria-label="checkbox button"
                    accessibilityLabel="checkbox button"
                    style={styles.checkboxButton}
                    leftIcon={
                      field.value ? (
                        <Checked
                          accessibilityLabel="checkbox button checked"
                          role="img"
                          alt="checkbox button checked"
                        />
                      ) : (
                        <UnChecked
                          accessibilityLabel="checkbox button unchecked"
                          role="img"
                          alt="checkbox button unchecked"
                        />
                      )
                    }
                    onPress={() => {
                      const newValue = !field.value;
                      field.onChange(newValue);
                      if (!newValue) {
                        setValue("expiresInDays", undefined);
                        clearErrors("expiresInDays");
                      }
                    }}
                  />
                )}
              />
              <CustomText
                text="Expiration days"
                size="m"
                weight="medium"
                useTranslationText={false}
                style={styles.label}
              />
            </View>
            <CustomText
              text="(Optional - minimum 1 days)"
              size="s"
              weight="regular"
              useTranslationText={false}
              style={styles.helperText}
            />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="expiresInDays"
              render={({ field }) => (
                <CustomTextInput
                  value={field.value ?? ""}
                  onChangeText={(value) => {
                    const sanitized = value.replace(/[^\d]/g, "");
                    field.onChange(sanitized === "" ? undefined : Number(sanitized));
                  }}
                  placeholder="Enter number of days"
                  keyboardType="numeric"
                  style={{ ...styles.input, backgroundColor: hasExpiration ? "white" : "#eee" }}
                  maxLength={3}
                  editable={hasExpiration}
                />
              )}
            />
            {!!errors.expiresInDays?.message && (
              <CustomText
                text={String(errors.expiresInDays.message)}
                size="s"
                style={styles.errorText}
                useTranslationText={false}
              />
            )}
          </View>
          <CustomSpacing type="vertical" size="l" />
          <CustomButton
            text={
              isSubmitting
                ? modalMode === ModalMode.UPDATE
                  ? "Updating..."
                  : "Generating..."
                : modalMode === ModalMode.UPDATE
                  ? "Update Link"
                  : "Generate Link"
            }
            styleType="primary"
            size="m"
            bold={true}
            onPress={onSubmit}
            useTranslationText={false}
            disabled={isSubmitting}
          />
          <CustomSpacing type="vertical" size="l" />
        </View>
      </CustomAdminModal>
      <ConfirmationModal
        visible={modalMode === ModalMode.DELETE}
        onClose={() => setModalMode(ModalMode.HIDDEN)}
        onConfirm={handleDeleteLink}
        message="Are you sure you want to delete this enrollment link? This action cannot be undone."
      />
      <CustomAdminModal
        visible={modalMode === ModalMode.ERROR}
        onRequestClose={() => setModalMode(ModalMode.HIDDEN)}
        title="Error"
        ariaLabel="Error modal"
        style={{}}>
        <View style={{ padding: 24 }}>
          <CustomText
            text={errors.maxEnrollments?.message || errors.expiresInDays?.message || "An error occurred"}
            size="m"
            style={styles.errorText}
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="l" />
          <CustomButton
            text="Close"
            onPress={() => setModalMode(ModalMode.HIDDEN)}
            styleType="primary"
            style={{ width: "100%" }}
          />
        </View>
      </CustomAdminModal>
      <CustomAdminModal
        visible={modalMode === ModalMode.SUCCESS}
        onRequestClose={() => setModalMode(ModalMode.HIDDEN)}
        title="Success"
        ariaLabel="Success modal"
        style={{}}>
        <View style={{ padding: 24 }}>
          <CustomText text={successMessage} size="m" style={{ color: "#059669", textAlign: "center" }} />
          <CustomSpacing type="vertical" size="l" />
          <CustomButton
            text="Close"
            onPress={() => setModalMode(ModalMode.HIDDEN)}
            styleType="primary"
            style={{ width: "100%" }}
          />
        </View>
      </CustomAdminModal>
    </>
  );
};

export default EnrollmentLinkManagerContainer;

const styles = StyleSheet.create({
  container: {
    padding: 24,
  },
  inputContainer: {
    marginBottom: 16,
  },
  errorText: {
    color: "#DC2626",
    marginBottom: 4,
  },
  label: {
    color: "#374151",
    marginBottom: 4,
  },
  helperText: {
    color: "#6B7280",
    fontStyle: "italic",
  },
  input: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16,
  },
  modal: {
    padding: 0,
  },
  checkboxButton: {
    padding: 0,
    marginRight: 10,
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
});
